import { useEffect, useRef, useState } from 'react';

// Diagnostic meta (timings, guard reason, raw un-guarded LLM reply) is only
// built in non-production builds — see the earlier review note: it can echo the
// system prompt / the un-guarded reply, so it must never render for real users.
const CHATBOT_DEBUG = process.env.NODE_ENV !== 'production';

// Reconnect backoff: each failed attempt waits longer (doubling, capped) with
// ±50% jitter so a server outage doesn't turn into a tight retry storm.
const supportEmail = window._env_?.REACT_APP_SUPPORT_EMAIL || '';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

const REPLY_TIMEOUT_MS = 15000;

// Build the diagnostic meta line shown under a bot reply in dev builds.
function buildMeta(data) {
    if (!CHATBOT_DEBUG) return '';
    const elapsed = data.elapsed ? `${(data.elapsed / 1000).toFixed(1)}s` : '';
    const timingsInfo = data.timings
        ? Object.entries(data.timings).map(([k, v]) => `${k}: ${(v / 1000).toFixed(1)}s`).join(' | ')
        : '';
    const guardInfo = data.guard
        ? `guard: ${data.guard.safe ? 'safe' : 'blocked — ' + data.guard.reason}`
        : '';
    const llmInfo = data.llmReply ? `LLM: "${data.llmReply}"` : '';
    return [elapsed, timingsInfo, guardInfo, llmInfo].filter(Boolean).join(' | ');
}

/**
 * Owns the chat WebSocket lifecycle so the Chatbot component can stay focused
 * on rendering + input. Handles connect, incoming messages, the loading flag,
 * the "Disconnected"/"Reconnected" system notices, exponential-backoff
 * reconnect, and clearing history on reset.
 *
 * @param {object}   params
 * @param {Function} params.setMessages  parent's message-list setter
 * @param {boolean}  params.loggedIn     re-opens the socket when it changes, so
 *        the handshake is routed to the correct handler on the server
 * @param {number}   params.chatReset    incremented by the parent to clear history
 * @returns {{ loading: boolean, isDisconnected: boolean, sendMessage: (text: string) => void, retryLastMessage: () => void }}
 */
export default function useChatSocket({ setMessages, loggedIn, chatReset }) {
    const wsRef = useRef(null);
    // Backoff bookkeeping: consecutive failed attempts + the pending retry timer.
    const reconnectRef = useRef({ attempts: 0, timer: null });
    const lastUserMsgRef = useRef(null);
    const replyTimerRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [isDisconnected, setIsDisconnected] = useState(false);

    function clearReplyTimer() {
        clearTimeout(replyTimerRef.current);
        replyTimerRef.current = null;
    }

    function armReplyTimer() {
        clearReplyTimer();
        replyTimerRef.current = setTimeout(() => {
            setLoading(false);
            setMessages(prev => [...prev, {
                id: Date.now(),
                sender: 'bot',
                text: 'Pixel could not respond.',
                retryable: true,
            }]);
        }, REPLY_TIMEOUT_MS);
    }

    // Clear history (e.g. New chat / page refresh) without tearing down the socket.
    useEffect(() => {
        if (chatReset > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'clear_history' }));
        }
    }, [chatReset]);

    // Tear down and reopen the socket whenever loggedIn changes, so we don't get
    // stuck in the wrong chat handler in chat.js.
    useEffect(() => {
        function scheduleReconnect() {
            const { attempts } = reconnectRef.current;
            const base = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * 2 ** attempts);
            const delay = base / 2 + Math.random() * (base / 2); // ±50% jitter
            reconnectRef.current.attempts = attempts + 1;
            clearTimeout(reconnectRef.current.timer);
            reconnectRef.current.timer = setTimeout(connect, delay);
        }

        function connect() {
            // A fresh attempt supersedes any pending backoff retry.
            clearTimeout(reconnectRef.current.timer);
            // Retire any socket we are replacing, so its onclose stays quiet.
            if (wsRef.current) {
                wsRef.current.closedByUs = true;
                wsRef.current.close();
            }
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const ws = new WebSocket(`${protocol}//${window.location.host}/api/v1/chat`);
            // A connect attempt is in flight — keep the input open (not locked)
            // so a mid-reconnect attempt doesn't block typing.
            setIsDisconnected(false);

            ws.onmessage = (event) => {
                clearReplyTimer();
                const data = JSON.parse(event.data);
                if (data.error) {
                    const suffix = supportEmail ? ` If the problem persists, contact [${supportEmail}](mailto:${supportEmail}).` : '';
                    setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text: `${data.error}${suffix}`, retryable: data.retryable }]);
                } else {
                    const botMessage = { id: Date.now(), sender: 'bot', text: data.reply, meta: buildMeta(data) };
                    setMessages(prev => [...prev, botMessage]);
                }
                setLoading(false);
            };

            ws.onclose = () => {
                clearReplyTimer();
                console.log('WebSocket disconnected');
                // A close we triggered ourselves (unmount — e.g. switching to
                // the FAQ view — or a loggedIn change) is not a lost
                // connection: the socket is reopened as soon as the chat view
                // comes back, so it must not leave a "Disconnected" notice
                // behind. `messages` lives in the parent and outlives this
                // component, so such a notice would otherwise be permanent.
                if (ws.closedByUs) return;
                // A genuine drop: lock the input until we're open again.
                setIsDisconnected(true);
                setMessages(prev => {
                    // Nothing to report before the user has said anything.
                    const hasUserMsg = prev.some(m => m.sender === 'user');
                    if (!hasUserMsg) return prev;
                    const last = prev[prev.length - 1];
                    if (last?.sender === 'system' && last.text === 'Disconnected') return prev;
                    return [...prev, { id: Date.now(), sender: 'system', text: 'Disconnected' }];
                });
                setLoading(false);
                // Server/network dropped us — keep trying to reconnect with
                // backoff so the chat self-heals once the backend is reachable.
                scheduleReconnect();
            };

            ws.onopen = () => {
                setIsDisconnected(false);
                console.log('Websocket connected, loggedIn =', loggedIn);
                // Connected — clear the backoff so the next drop starts from
                // the base delay again.
                reconnectRef.current.attempts = 0;
                // Clear a pending "Disconnected" notice. It lives in the
                // parent's `messages`, so it can predate this mount (a drop,
                // then a trip through the FAQ view). With no notice pending
                // this is a no-op.
                setMessages(prev => {
                    const last = prev[prev.length - 1];
                    if (last?.sender === 'system' && last.text === 'Disconnected') {
                        return [...prev.slice(0, -1), { id: Date.now(), sender: 'system', text: 'Reconnected' }];
                    }
                    return prev;
                });
            };

            ws.onerror = () => {
                console.log("Websocket error");
            };

            wsRef.current = ws;
        }

        connect();

        function handleOnline() {
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                console.log('Network back — reconnecting WebSocket');
                // A genuine connectivity event: retry now, not after backoff.
                reconnectRef.current.attempts = 0;
                connect();
            }
        }

        function handleVisibilityChange() {
            if (document.visibilityState === 'visible' && wsRef.current?.readyState !== WebSocket.OPEN) {
                console.log('Page visible again — reconnecting WebSocket');
                // A genuine connectivity event: retry now, not after backoff.
                reconnectRef.current.attempts = 0;
                connect();
            }
        }

        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // Cancel any pending backoff retry so it can't fire after unmount.
            clearTimeout(reconnectRef.current.timer);
            clearReplyTimer();
            if (wsRef.current) {
                wsRef.current.closedByUs = true;
                wsRef.current.close();
            }
        };
    }, [loggedIn, setMessages]);

    // Put a user message on the wire. Returns false (without sending) if the
    // socket isn't open, so the caller can avoid showing a bubble that would
    // never get a reply.
    const sendMessage = (text) => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) return false;
        setLoading(true);
        armReplyTimer();
        lastUserMsgRef.current = text;
        wsRef.current.send(JSON.stringify({ message: text }));
        return true;
    };

    // Resend the last user message (the "Try again" affordance on an error).
    const retryLastMessage = () => {
        const lastMsg = lastUserMsgRef.current;
        if (!lastMsg || loading) return;
        if (wsRef.current?.readyState !== WebSocket.OPEN) return;
        setLoading(true);
        armReplyTimer();
        wsRef.current.send(JSON.stringify({ message: lastMsg }));
    };

    return { loading, isDisconnected, sendMessage, retryLastMessage };
}
